import { AxiosRequestConfig } from 'axios';
import qs from 'qs';
import type {
  RequireAtLeastOne,
  SnakeCasedProperties,
  SnakeCasedPropertiesDeep,
} from 'type-fest';
import type {
  AsyncDocumentSuggestionResponse,
  Document,
  DocumentSplitSuggestionResponse,
  FillConfig,
  FormMatch,
} from 'src/types/proto/documents';
import type { FormConfig } from 'src/types/proto/reform';
import type {
  SearchFormPublicResponse,
  SearchFormRequest,
  SearchFormResponse,
} from 'src/types/proto/services/document_form_public_service';
import type {
  CreateDocumentRequest,
  GetFormSuggestionsRequest,
  GetFormsByGfpFlowIdResponse,
  ResizeRequest,
  UploadDocumentsResponse,
  ZipEnvelopeDocumentsRequest,
} from 'src/types/proto/services/document_public_service';
import type {
  GetDocumentByUrlRequest,
  GetEditableDocumentByTdvRequest,
  GetEditableDocumentByTdvResponse,
  PspdfkitDocument,
} from 'src/types/proto/services/pspdfkit_public_service';
import type { ZipTransactionDocumentVersionsRequest } from 'src/types/proto/services/transaction_public_service';
import type { ApiResponseData } from 'src/types/utils';
import { thresholdConcurrentRequests } from 'src/utils/concurrent-requests';
import { downloadBlob } from 'src/utils/download-blob';
import { formatToSnakeCase } from 'src/utils/format-data-object';
import BaseApi from '../base-api';
import type { ZipFileOptions } from './shared-packet';

export interface UploadOptions {
  asPDF?: boolean;
}

export interface FetchPspdfkitDocumentOptions {
  isCoverSheetDoc?: boolean;
}

interface ThumbnailOptions {
  page?: number;
  width?: number;
}

export default class Documents extends BaseApi {
  createDocument(
    file: Omit<CreateDocumentRequest, 'analyze' | 'decrypt'>,
    analyze = false,
    decrypt = false
  ) {
    return this.post<Document>('/documents', {
      url: file.url,
      filename: file.filename,
      byteSize: file.byteSize,
      thumbnailUrl: file.thumbnailUrl,
      analyze,
      decrypt,
    });
  }

  searchForms = ({
    query,
    libraryUuids,
    state,
    county,
  }: RequireAtLeastOne<SearchFormRequest, 'libraryUuids' | 'state'>) => {
    return this.get<
      ApiResponseData<SearchFormResponse | SearchFormPublicResponse>
    >('/documents/forms/search', {
      params: {
        query,
        ...(libraryUuids
          ? {
              library_uuids: libraryUuids,
            }
          : null),
        ...(state
          ? {
              state,
              county,
            }
          : null),
      },
    });
  };

  getDownloadCfgByDocToken(docToken: string) {
    return {
      url: this.axios.getUri({
        url: '/documents/download_by_doc_token',
        params: formatToSnakeCase({
          docToken,
        }),
      }),
      api: this,
    };
  }

  getFlowAttachmentDownloadCfg(flowId: string, documentId: string) {
    return {
      url: this.axios.getUri({
        url: `/documents/flow/${flowId}/download_attachment`,
        params: formatToSnakeCase({
          documentId,
        }),
      }),
      api: this,
    };
  }

  getDownloadCfgByTdId(tdId: string) {
    return {
      url: this.axios.getUri({
        url: '/documents/download',
        params: formatToSnakeCase({
          tdId,
        }),
      }),
      api: this,
    };
  }

  getThumbnailBlobByTdId(tdId: string, options?: ThumbnailOptions) {
    const { page = 1, width = 30 } = options || {};
    return this.get<Blob>('/documents/thumbnail', {
      responseType: 'blob',
      params: formatToSnakeCase({
        tdId,
        page,
        width: Math.round(width),
      }),
    });
  }

  throttledGetThumbnailBlobByTdId = thresholdConcurrentRequests(
    this.getThumbnailBlobByTdId.bind(this),
    (tdId, options = {}) => {
      const { width = 30 } = options;
      return `getThumbnailBlobByTdId:${JSON.stringify(
        formatToSnakeCase({
          tdId,
          width: Math.round(width),
        })
      )}`;
    },
    {
      ttl: 10 * 60 * 1000, // 10 minutes
    }
  );

  getThumbnailBlobByTdvId(tdvId: string) {
    return this.get<Blob>('/documents/tdv/thumbnail', {
      responseType: 'blob',
      params: formatToSnakeCase({
        tdvId,
      }),
    });
  }

  getDownloadCfgByTdvId(tdvId: string) {
    return {
      url: this.axios.getUri({
        url: '/documents/tdv/download',
        params: formatToSnakeCase({
          tdvId,
        }),
      }),
      api: this,
    };
  }

  getThumbnailBlobByGfpfId(txnId: string, gfpfId: string, preview: boolean) {
    return this.get<Blob>(`/documents/txn/${txnId}/gfpf/${gfpfId}/thumbnail`, {
      responseType: 'blob',
      params: {
        preview,
      },
    });
  }

  getDownloadCfgByGfpfId(txnId: string, gfpfId: string, preview: boolean) {
    return {
      url: this.axios.getUri({
        url: `/documents/txn/${txnId}/gfpf/${gfpfId}/download`,
        params: {
          preview,
        },
      }),
      api: this,
    };
  }

  getFormFlowThumbnailBlob(flowId: string) {
    return this.get<Blob>(`/documents/flow/${flowId}/form_thumbnail`, {
      responseType: 'blob',
    });
  }

  getFormFlowDocumentPreviewBlob(flowId: string) {
    return this.get<Blob>(`/documents/flow/${flowId}/form_preview`, {
      responseType: 'blob',
    });
  }

  async downloadFormFlowDocumentPreview(flowId: string, filename: string) {
    const blob = await this.getFormFlowDocumentPreviewBlob(flowId);
    await downloadBlob(blob, filename);
  }

  getZipDownloadUrlByTdIds(
    tdIds: string[],
    zipFilename: string,
    options: ZipFileOptions = {}
  ) {
    const { numberDocuments = false } = options;
    return this.axios.getUri({
      url: '/documents/zip_on_the_fly',
      params: formatToSnakeCase({
        tdIds,
        output: zipFilename,
        numberDocuments,
      }),
    });
  }

  getZipBlobByTdIds(
    tdIds: string[],
    zipFilename: string,
    options: ZipFileOptions = {}
  ) {
    return this.get<Blob>(
      this.getZipDownloadUrlByTdIds(tdIds, zipFilename, options),
      { responseType: 'blob' }
    );
  }

  async downloadMultipleDocsAsZip(
    tdIds: string[],
    zipFilename: string,
    options: ZipFileOptions = {}
  ) {
    const blob = await this.getZipBlobByTdIds(tdIds, zipFilename, options);
    await downloadBlob(blob, zipFilename);
  }

  getEnvelopeDocumentDownloadCfg(edId: string) {
    return {
      url: this.axios.getUri({
        url: '/documents/download_envelope_document',
        params: formatToSnakeCase({
          edId,
        }),
      }),
      api: this,
    };
  }

  getZipEnvelopeDocumentsUrl(
    envelopeId: string,
    edIds: string[],
    zipFilename: string,
    includeSigningCertificate = false,
    options: ZipFileOptions = {}
  ) {
    const { numberDocuments = false } = options;

    return this.axios.getUri({
      url: '/documents/zip_env_docs',
      params: formatToSnakeCase({
        envelopeId,
        edIds,
        output: zipFilename,
        includeSigningCertificate,
        numberDocuments,
      } as ZipEnvelopeDocumentsRequest),
    });
  }

  getZipEnvelopeDocumentsBlob(
    envelopeId: string,
    edIds: string[],
    zipFilename: string,
    includeSigningCertificate = false,
    options: ZipFileOptions = {}
  ) {
    return this.get<Blob>(
      this.getZipEnvelopeDocumentsUrl(
        envelopeId,
        edIds,
        zipFilename,
        includeSigningCertificate,
        options
      ),
      { responseType: 'blob' }
    );
  }

  async downloadEnvelopeDocuments(
    envelopeId: string,
    edIds: string[],
    zipFilename: string,
    includeSigningCertificate = false,
    options: ZipFileOptions = {}
  ) {
    const blob = await this.getZipEnvelopeDocumentsBlob(
      envelopeId,
      edIds,
      zipFilename,
      includeSigningCertificate,
      options
    );
    await downloadBlob(blob, zipFilename);
  }

  async downloadMultipleTdvs(
    tdvIds: string[],
    filename: string,
    numberDocuments: boolean
  ) {
    const blob = await this.getZipBlobByTdvIdsV2(
      tdvIds,
      filename,
      numberDocuments
    );
    await downloadBlob(blob, filename);
  }

  /**
   * Builds zip from TDV IDs using webapp to file-service.
   */
  getZipDownloadUrlByTdvIdsV2(
    tdvIds: string[],
    filename: string,
    numberDocuments: boolean
  ) {
    return this.axios.getUri({
      url: `/documents/zip_on_the_fly_by_tdv_ids`,
      params: {
        tdv_ids: tdvIds,
        output: filename,
        number_documents: numberDocuments,
      },
    });
  }

  /**
   * Builds zip from TDV IDs using webapp to file-service.
   */
  getZipBlobByTdvIdsV2(
    tdvIds: string[],
    filename: string,
    numberDocuments: boolean
  ): Promise<Blob> {
    return this.get<Blob>(
      this.getZipDownloadUrlByTdvIdsV2(tdvIds, filename, numberDocuments),
      { responseType: 'blob' }
    );
  }

  resizeImage(params: ResizeRequest) {
    return this.get<Blob>(`/documents/resize`, {
      responseType: 'blob',
      params: formatToSnakeCase(params),
    });
  }

  getFormSuggestions<T extends GetFormSuggestionsRequest>({
    tdIds,
    tdvIds,
    transactionId,
    mode = 'tabbing',
  }: T) {
    type Suggestion = T['mode'] extends 'splitting'
      ? DocumentSplitSuggestionResponse
      : FormMatch;

    return this.get<Record<string, Suggestion>>(
      `/documents/suggestions/${transactionId}`,
      {
        params: {
          td_ids: tdIds && tdIds.length ? tdIds : undefined,
          tdv_ids: tdvIds && tdvIds.length ? tdvIds : undefined,
          mode,
        },
      }
    );
  }

  getFormSuggestionsAsync({
    tdIds,
    tdvIds,
    transactionId,
    mode = 'tabbing',
  }: GetFormSuggestionsRequest) {
    return this.get<AsyncDocumentSuggestionResponse>(
      `/documents/suggestions/${transactionId}/async`,
      {
        params: {
          td_ids: tdIds && tdIds.length ? tdIds : undefined,
          tdv_ids: tdvIds && tdvIds.length ? tdvIds : undefined,
          mode,
        },
      }
    );
  }

  getGfpForms(gfpFlowId: string) {
    return this.get<ApiResponseData<GetFormsByGfpFlowIdResponse>>(
      `/documents/gfp_forms/${encodeURIComponent(gfpFlowId)}`
    );
  }

  getPspdfkitDocumentByForm(formId: string) {
    return this.get<SnakeCasedProperties<PspdfkitDocument>>(
      `/pspdfkit/document/by_form/${encodeURIComponent(formId)}`
    );
  }

  getPspdfkitDocumentByDoc(
    documentUuid: string,
    options: FetchPspdfkitDocumentOptions = {}
  ) {
    const { isCoverSheetDoc = false } = options || {};
    return this.get<SnakeCasedProperties<PspdfkitDocument>>(
      `/pspdfkit/document/by_doc/${encodeURIComponent(documentUuid)}${
        isCoverSheetDoc ? '?cover-sheet-doc=true' : ''
      }`
    );
  }

  getPspdfkitDocumentByUrl(
    url: string,
    options: FetchPspdfkitDocumentOptions = {}
  ) {
    const { isCoverSheetDoc = false } = options || {};
    return this.post<SnakeCasedProperties<PspdfkitDocument>>(
      `/pspdfkit/document/by_url${
        isCoverSheetDoc ? '?cover-sheet-doc=true' : ''
      }`,
      {
        url,
      } as GetDocumentByUrlRequest
    );
  }

  getFillConfigByFormUuid(formUuid: string, tdId?: string) {
    return this.get<FillConfig>(
      `/forms/${encodeURIComponent(formUuid)}/fill_config`,
      tdId
        ? {
            params: {
              td_id: tdId,
            },
          }
        : undefined
    );
  }

  uploadFileProxied(
    formData: FormData,
    options: UploadOptions = {},
    config?: AxiosRequestConfig
  ) {
    return this.post<UploadDocumentsResponse>('/documents/upload', formData, {
      params: formatToSnakeCase(options),
      ...config,
    });
  }

  getPspdfkitEditableDocumentByTdv(transactionId: string, tdvId: string) {
    return this.post<
      SnakeCasedPropertiesDeep<GetEditableDocumentByTdvResponse>
    >(
      '/pspdfkit/document/editable_by_tdv',
      formatToSnakeCase({
        transactionId,
        tdvId,
      } as GetEditableDocumentByTdvRequest)
    );
  }

  getFormConfig(formUuid: string) {
    return this.get<FormConfig>(
      `/forms/${encodeURIComponent(formUuid)}/form_config`
    );
  }

  revokeEmailedDocumentsAccess(emailId: string) {
    return this.post<unknown>(`/documents/revoke/${emailId}`, {});
  }
}
